import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Eye, EyeOff, CheckCircle, Mail, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "United Kingdom", "United States", "Ireland", "Canada", "Australia",
  "Germany", "France", "Spain", "Italy", "Netherlands", "Belgium",
  "Sweden", "Norway", "Denmark", "India", "Singapore", "New Zealand", "Other",
];

// --- Post-signup "Check your email" view ---
const VerificationPendingView = ({ email, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const { toast } = useToast();

  const handleResend = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResent(true);
      toast({ title: "Verification email sent", description: `A new link has been sent to ${email}.` });
    } catch (err) {
      toast({
        title: "Could not resend",
        description: err.response?.data?.detail || "Please try again shortly.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md text-center" data-testid="verification-pending-view">
      <div className="mx-auto w-16 h-16 rounded-full bg-pink-50 flex items-center justify-center mb-6">
        <Mail className="w-8 h-8 text-[#e6007e]" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-3 font-heading">Verify your email</h2>
      <p className="text-gray-600 font-body mb-6">
        We've sent a verification link to <strong>{email}</strong>. Please click the link in that email to activate your account.
      </p>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600 font-body mb-6">
        <p className="mb-2"><ShieldCheck className="inline w-4 h-4 text-[#e6007e] mr-1" /> Your account is secured — you won't be able to sign in until your email is verified.</p>
        <p>The link expires in 24 hours. Can't find it? Check your spam folder.</p>
      </div>
      <Button
        type="button"
        onClick={handleResend}
        disabled={loading || resent}
        className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium font-body mb-3"
        data-testid="resend-verification-btn"
      >
        {loading ? "Sending..." : resent ? "Verification email sent" : "Resend verification email"}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-gray-600 hover:text-gray-900 underline font-body"
        data-testid="back-to-login-btn"
      >
        Back to sign in
      </button>
    </div>
  );
};

export const AuthScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    businessName: "",
    contactName: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
    companyNumber: "",
    website: "",
  });
  const [loading, setLoading] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
  const [unverifiedLoginEmail, setUnverifiedLoginEmail] = useState(null);
  const { login, register, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleInput = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedLoginEmail(null);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate("/", { replace: true });
      } else {
        // Basic client-side validation
        if (!formData.phone || formData.phone.trim().length < 7) {
          toast({ title: "Phone required", description: "Please enter a valid business phone number.", variant: "destructive" });
          setLoading(false); return;
        }
        if (formData.website && !/^https?:\/\//i.test(formData.website)) {
          toast({ title: "Invalid website", description: "Website must start with http:// or https://", variant: "destructive" });
          setLoading(false); return;
        }
        const result = await register({
          email: formData.email,
          password: formData.password,
          business_name: formData.businessName,
          contact_name: formData.contactName,
          phone: formData.phone,
          business_address_line1: formData.addressLine1,
          business_address_line2: formData.addressLine2 || null,
          business_city: formData.city,
          business_postcode: formData.postcode,
          business_country: formData.country,
          company_number: formData.companyNumber || null,
          website: formData.website || null,
        });
        // Registration no longer auto-logs in — show verification pending screen
        setPendingVerificationEmail(result?.email || formData.email);
      }
    } catch (error) {
      let errorMessage = "Authentication failed";
      if (error.response?.data?.detail) errorMessage = error.response.data.detail;
      else if (error.message) errorMessage = error.message;
      // Special-case unverified email
      if (typeof errorMessage === "string" && errorMessage.startsWith("EMAIL_NOT_VERIFIED")) {
        setUnverifiedLoginEmail(formData.email);
        toast({
          title: "Email not verified",
          description: "Please verify your email before signing in. We can resend the link.",
          variant: "destructive",
        });
      } else {
        toast({ title: isLogin ? "Login Error" : "Sign up failed", description: errorMessage, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendFromLogin = async () => {
    if (!unverifiedLoginEmail) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: unverifiedLoginEmail });
      toast({ title: "Verification email sent", description: `A new link has been sent to ${unverifiedLoginEmail}.` });
    } catch (err) {
      toast({ title: "Could not resend", description: err.response?.data?.detail || "Please try again shortly.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div className="hidden lg:flex lg:w-1/2 bg-black text-white flex-col justify-center px-16">
        <div className="max-w-md">
          <img src="/paify-logo.png" alt="Paify" className="h-24 mb-8" />
          <h1 className="text-5xl font-bold mb-8 leading-tight font-heading">
            Structured.<br />Protected.<br /><span className="text-[#e6007e]">Paid.</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed font-body">
            The checkout for the service industry. Structure your payment flow with confidence.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-8 py-12 overflow-y-auto">
        {pendingVerificationEmail ? (
          <VerificationPendingView
            email={pendingVerificationEmail}
            onBack={() => { setPendingVerificationEmail(null); setIsLogin(true); }}
          />
        ) : (
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-12 text-center">
              <img src="/paify-logo.png" alt="Paify" className="h-16 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-4 font-heading">
                Structured. Protected. <span className="text-[#e6007e]">Paid.</span>
              </h1>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 font-heading">
                {isLogin ? "Welcome back" : "Create your business account"}
              </h2>
              <p className="text-gray-600 font-body">
                {isLogin
                  ? "Sign in to your account"
                  : "Secure signup — your business details help us protect against fraud and power your contracts & invoices."}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 font-body">Business Email</Label>
                <Input
                  id="email" type="email" value={formData.email}
                  onChange={handleInput("email")}
                  className="mt-1 h-12 font-body" placeholder="your@business.com" required
                  data-testid="signup-email-input"
                />
              </div>

              {/* Signup-only fields */}
              {!isLogin && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="businessName" className="text-sm font-medium text-gray-700 font-body">
                        Business Name <span className="text-red-500">*</span>
                      </Label>
                      <Input id="businessName" value={formData.businessName} onChange={handleInput("businessName")}
                        className="mt-1 h-12 font-body" placeholder="Your Business Ltd" required
                        data-testid="signup-business-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="contactName" className="text-sm font-medium text-gray-700 font-body">
                        Your Name <span className="text-red-500">*</span>
                      </Label>
                      <Input id="contactName" value={formData.contactName} onChange={handleInput("contactName")}
                        className="mt-1 h-12 font-body" placeholder="John Doe" required
                        data-testid="signup-contact-name-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700 font-body">
                      Business Phone <span className="text-red-500">*</span>
                    </Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={handleInput("phone")}
                      className="mt-1 h-12 font-body" placeholder="+44 20 1234 5678" required
                      data-testid="signup-phone-input" />
                  </div>

                  <div className="pt-2">
                    <p className="text-sm font-semibold text-gray-800 mb-3 font-body">
                      Business Address <span className="text-red-500">*</span>
                    </p>
                    <div className="space-y-3">
                      <Input value={formData.addressLine1} onChange={handleInput("addressLine1")}
                        className="h-12 font-body" placeholder="Address line 1" required
                        data-testid="signup-address-line1-input" />
                      <Input value={formData.addressLine2} onChange={handleInput("addressLine2")}
                        className="h-12 font-body" placeholder="Address line 2 (optional)"
                        data-testid="signup-address-line2-input" />
                      <div className="grid grid-cols-2 gap-3">
                        <Input value={formData.city} onChange={handleInput("city")}
                          className="h-12 font-body" placeholder="City" required
                          data-testid="signup-city-input" />
                        <Input value={formData.postcode} onChange={handleInput("postcode")}
                          className="h-12 font-body" placeholder="Postcode / ZIP" required
                          data-testid="signup-postcode-input" />
                      </div>
                      <select
                        value={formData.country}
                        onChange={handleInput("country")}
                        className="w-full h-12 border border-gray-300 rounded-md px-3 font-body bg-white"
                        data-testid="signup-country-select"
                      >
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="companyNumber" className="text-sm font-medium text-gray-700 font-body">
                        Company Number <span className="text-gray-400 text-xs">(optional)</span>
                      </Label>
                      <Input id="companyNumber" value={formData.companyNumber} onChange={handleInput("companyNumber")}
                        className="mt-1 h-12 font-body" placeholder="12345678"
                        data-testid="signup-company-number-input" />
                    </div>
                    <div>
                      <Label htmlFor="website" className="text-sm font-medium text-gray-700 font-body">
                        Website <span className="text-gray-400 text-xs">(optional)</span>
                      </Label>
                      <Input id="website" type="url" value={formData.website} onChange={handleInput("website")}
                        className="mt-1 h-12 font-body" placeholder="https://yourbusiness.com"
                        data-testid="signup-website-input" />
                    </div>
                  </div>
                </>
              )}

              {/* Password */}
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 font-body">Password</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? "text" : "password"}
                    value={formData.password} onChange={handleInput("password")}
                    className="mt-1 h-12 font-body pr-12" placeholder="••••••••" required
                    data-testid="signup-password-input"
                  />
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center mt-1 hover:bg-transparent">
                    {showPassword ? <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" /> : <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />}
                  </Button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    Min. 8 characters, with uppercase, lowercase, and a number.
                  </p>
                )}
              </div>

              {isLogin && (
                <div className="flex justify-end">
                  <button type="button" onClick={() => window.location.href = "/forgot-password"}
                    className="text-sm text-gray-600 hover:text-gray-900 underline font-body">
                    Forgot password?
                  </button>
                </div>
              )}

              {unverifiedLoginEmail && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm font-body" data-testid="unverified-login-banner">
                  <p className="text-amber-900 mb-2">
                    <strong>{unverifiedLoginEmail}</strong> hasn't been verified yet.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendFromLogin}
                    className="text-[#e6007e] hover:underline font-medium"
                    data-testid="unverified-resend-link"
                  >
                    Resend verification email →
                  </button>
                </div>
              )}

              <Button type="submit"
                className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium font-body"
                disabled={loading}
                data-testid={isLogin ? "login-submit-btn" : "signup-submit-btn"}>
                {loading ? "Please wait..." : (isLogin ? "Sign in" : "Create my account")}
              </Button>

              {!isLogin && (
                <p className="text-xs text-gray-500 text-center font-body">
                  By creating an account you agree to our{" "}
                  <a href="/terms-of-service" className="underline">Terms</a> and{" "}
                  <a href="/privacy-policy" className="underline">Privacy Policy</a>.
                </p>
              )}
            </form>

            <div className="mt-6 text-center">
              <button type="button"
                onClick={() => { setIsLogin(!isLogin); setUnverifiedLoginEmail(null); }}
                className="text-sm text-gray-600 hover:text-gray-900 underline font-body"
                data-testid="toggle-auth-mode-btn">
                {isLogin ? "New to Paify? Create account" : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast({ title: "Email Sent!", description: "If an account exists with that email, you'll receive a password reset link." });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send reset email", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Forgot Password?</h1>
          <p className="text-gray-600 font-body">
            {sent ? "Check your email for a reset link" : "Enter your email to receive a password reset link"}
          </p>
        </div>
        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700 font-body">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-12 font-body" placeholder="your@email.com" required />
            </div>
            <Button type="submit" className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium font-body" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <p className="text-gray-700 font-body mb-4">We've sent a password reset link to <strong>{email}</strong></p>
            <p className="text-sm text-gray-600 font-body">The link will expire in 1 hour. Didn't receive it? Check your spam folder.</p>
          </div>
        )}
        <div className="mt-6 text-center">
          <a href="/login" className="text-sm text-gray-600 hover:text-gray-900 underline font-body">Back to login</a>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [formData, setFormData] = useState({ new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) {
      toast({ title: "Error", description: "Passwords don't match", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: formData.new_password });
      setSuccess(true);
      toast({ title: "Success!", description: "Your password has been reset. Redirecting to login..." });
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to reset password", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4 font-heading">Invalid Reset Link</h1>
          <p className="text-gray-600 font-body mb-4">This password reset link is invalid or has expired.</p>
          <a href="/forgot-password" className="text-sm text-gray-600 hover:text-gray-900 underline font-body">Request a new reset link</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Password Reset Successfully!</h1>
          <p className="text-gray-600 font-body">Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Reset Your Password</h1>
          <p className="text-gray-600 font-body">Enter your new password below</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="new_password" className="text-sm font-medium text-gray-700 font-body">New Password</Label>
            <div className="relative">
              <Input id="new_password" type={showPassword ? "text" : "password"} value={formData.new_password} onChange={(e) => setFormData({ ...formData, new_password: e.target.value })} className="mt-1 h-12 font-body pr-12" placeholder="••••••••" required />
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center mt-1 hover:bg-transparent">
                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" /> : <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1 font-body">Must be at least 8 characters with uppercase, lowercase, and a number</p>
          </div>
          <div>
            <Label htmlFor="confirm_password" className="text-sm font-medium text-gray-700 font-body">Confirm New Password</Label>
            <div className="relative">
              <Input id="confirm_password" type={showPassword ? "text" : "password"} value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })} className="mt-1 h-12 font-body pr-12" placeholder="••••••••" required />
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-3 flex items-center mt-1 hover:bg-transparent">
                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" /> : <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />}
              </Button>
            </div>
          </div>
          <Button type="submit" className="w-full h-12 bg-black hover:bg-gray-800 text-white font-medium font-body" disabled={loading}>
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (token) { verifyEmail(); }
    else { setLoading(false); setError("Invalid verification link"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.get(`${API}/auth/verify-email/${token}`);
      setVerified(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 font-body">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center" data-testid="email-verified-success">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Email Verified!</h1>
          <p className="text-gray-600 font-body mb-4">Your email has been successfully verified.</p>
          <p className="text-gray-600 font-body">Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="text-center" data-testid="email-verified-error">
        <h1 className="text-2xl font-bold text-red-600 mb-4 font-heading">Verification Failed</h1>
        <p className="text-gray-600 font-body mb-4">{error}</p>
        <a href="/login" className="text-sm text-gray-600 hover:text-gray-900 underline font-body">Back to login</a>
      </div>
    </div>
  );
};
